// src/components/StockTable.js
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, BarChart3, Bell, Eye, RefreshCw } from 'lucide-react';
import StockDashboard from '../../utils/StockDashboard'

const StockTable = ({ portfolio, darkMode, onRefresh, loading, realTimePrices = {} }) => {
  const navigate = useNavigate();
  // Safe function to get current price with fallback
  const getCurrentPrice = (stock) => {
    return realTimePrices[stock.symbol] !== undefined 
      ? realTimePrices[stock.symbol] 
      : stock.market_price || stock.currentPrice || 0;
  };

  // Calculate derived values safely
  const calculateMarketValue = (stock) => {
    const currentPrice = getCurrentPrice(stock);
    return stock.quantity * currentPrice;
  };

  const calculateUnrealizedPnl = (stock) => {
    const currentPrice = getCurrentPrice(stock);
    return (currentPrice - stock.average_cost) * stock.quantity;
  };

  const calculatePnlPercentage = (stock) => {
    const currentPrice = getCurrentPrice(stock);
    return stock.average_cost > 0 
      ? ((currentPrice - stock.average_cost) / stock.average_cost) * 100 
      : 0;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
//      minimumFractionDigits: 2,
//      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value}%`;
  };


  const getRSAData = (symbol) => {
    const rsaValues = {
      'AAPL': { value: 65, status: 'overbought' },
      'GOOGL': { value: 35, status: 'oversold' },
      'TSLA': { value: 72, status: 'overbought' },
      'MSFT': { value: 45, status: 'neutral' },
      'AMZN': { value: 28, status: 'oversold' },
      'NVDA': { value: 68, status: 'overbought' },
      'JPM': { value: 52, status: 'neutral' },
      'V': { value: 38, status: 'oversold' },
      'DIS': { value: 61, status: 'overbought' },
      'NFLX': { value: 42, status: 'neutral' }
    };
    return rsaValues[symbol] || { value: 50, status: 'neutral' };
  };

  // const handleSetAlert = (symbol, price) => {
  //   const targetPrice = prompt(`Set price alert for ${symbol}:`, price);
  //   if (targetPrice) {
  //     alert(`Alert set for ${symbol} at ${formatCurrency(targetPrice)}`);
  //   }
  // };
  // Add handleButtonClick for table actions
  const handleButtonClick = async (action, symbol, event) => {
    try {
      event.preventDefault();
      event.stopPropagation();
      
      console.log(`Action: ${action}, Symbol: ${symbol}`);
      
      switch (action) {
        case 'setAlert':
          await handleSetAlert(symbol, event);
          break;
        case 'viewAnalysis':
            <div className="overflow-x-auto">
              <StockDashboard ticker="TSLA" period="1mo" />
            </div>          
//          await handleViewAnalysis(symbol, event);
//          break;
        case 'refresh':
          onRefresh();
          break;
        default:
          console.warn('Unknown action:', action);
      }
    } catch (error) {
      console.error('Error in button click:', error);
      // Don't throw the event or error
    }
  };

  const handleSetAlert = async (symbol, event) => {
    try {
      event.preventDefault();
      const price = realTimePrices[symbol] || 100;
      const targetPrice = prompt(`Set price alert for ${symbol}:`, price.toFixed(2));
      if (targetPrice) {
        alert(`Alert set for ${symbol} at $${targetPrice}`);
      }
    } catch (error) {
      console.error('Error setting alert:', error);
    }
  };

  const handleViewAnalysis = async (symbol, event) => {
    try {
      event.preventDefault();
//      event.stopPropagation();
      // Implement analysis view logic
      alert(`Alert set for ${symbol} goto analytical result`);
      
      console.log(`Viewing analysis for: ${symbol}`);
      // You might want to set state to show a modal or navigate
      navigate(`/stockAnalysis/${symbol.trim().toUpperCase()}`);
    } catch (error) {
      console.error('Error viewing analysis:', error);
    }
  };  

  const getRowBackgroundColor = (symbol) => {
    const rsaData = getRSAData(symbol);
    if (rsaData.status === 'overbought') {
      return darkMode ? 'bg-red-900/20 hover:bg-red-900/30' : 'bg-red-50 hover:bg-red-100';
    } else if (rsaData.status === 'oversold') {
      return darkMode ? 'bg-green-900/20 hover:bg-green-900/30' : 'bg-green-50 hover:bg-green-100';
    }
    return darkMode ? 'bg-gray-700/50 hover:bg-gray-700/70' : 'bg-white hover:bg-gray-50';
  };

  // Filter out stocks with zero quantity
  // const isPositive = false;
  // if (portfolio.map(stock => stock.quantity > 0)) {
  //   portfolio = portfolio.filter(stock => stock.quantity > 0);
  //   isPositive = portfolio.every(stock => stock.unrealizedPnl >= 0);
  // }


  // Enhanced portfolio data with real-time prices
  const enhancedPortfolio = portfolio.map(stock => {
    const currentPrice = getCurrentPrice(stock);
    const marketValue = calculateMarketValue(stock);
    const unrealizedPnl = calculateUnrealizedPnl(stock);
    const pnlPercentage = calculatePnlPercentage(stock);

    return {
      ...stock,
      currentPrice,
      marketValue,
      unrealizedPnl,
      pnlPercentage,
      hasRealTimeData: realTimePrices[stock.symbol] !== undefined
    };
  });

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg`}>
        <div className="text-center">
          <RefreshCw className="animate-spin h-8 w-8 mx-auto mb-4 text-blue-600" />
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Loading portfolio data...</p>
        </div>
      </div>
    );
  }  

  return (
    <div className={`rounded-lg shadow-lg ${
      darkMode ? 'bg-gray-800' : 'bg-white'
    } mt-6`}>
      {/* Table Header with Refresh Button */}
      <div className={`flex justify-between items-center p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h3 className="text-lg font-semibold">Portfolio Holdings</h3>
        <button
          onClick={onRefresh}
          disabled={loading}
          className={`flex items-center px-3 py-2 rounded text-sm ${
            darkMode
              ? 'bg-blue-700 text-white hover:bg-blue-600 disabled:bg-blue-800'
              : 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400'
          }`}
        >
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>      
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                Symbol
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                Qty
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                Avg Cost
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                Current Price
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                Market Value
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                P&L
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                Change
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                RSA
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {portfolio.map((stock, index) => {
              const profitLoss = stock.unrealizedPnl;
              const profitLossPercent = ((stock.currentPrice - stock.average_cost) / stock.average_cost) * 100;
              const rsaData = getRSAData(stock.symbol);
              const isPositive = stock.unrealizedPnl >= 0;

              return (
                <tr 
                  key={`${stock.symbol}-${index}`} 
                  className={getRowBackgroundColor(stock.symbol)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-bold">{stock.symbol}</div>
                      {stock.hasRealTimeData && (
                        <span className="ml-2 w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Real-time data"></span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">{stock.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">{stock.quantity}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">{formatCurrency(stock.average_cost)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold">
                      {formatCurrency(stock.currentPrice)}
                      {!stock.hasRealTimeData && (
                        <span className="ml-1 text-xs text-gray-500" title="Delayed data">*</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold">
                      {formatCurrency(stock.marketValue)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(stock.unrealizedPnl)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercentage(stock.pnlPercentage)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      rsaData.status === 'overbought' 
                        ? darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-800'
                        : rsaData.status === 'oversold'
                        ? darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800'
                        : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {rsaData.status} ({rsaData.value})
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => handleSetAlert(stock.symbol, e)}
                        className={`p-1 rounded ${
                          darkMode 
                            ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
                            : 'bg-yellow-500 text-white hover:bg-yellow-600'
                        }`}
                        title="Set Price Alert"
                      >
                        <Bell size={14} />
                      </button>
                      <button
                        onClick={(e) => handleViewAnalysis(stock.symbol, e)}
                        className={`p-1 rounded ${
                          darkMode 
                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                        title="View Analysis"
                      >
                        <Link to="/stockAnalysis/${ticker.trim().toUpperCase()}">
                          <BarChart3 size={14} />
                        </Link>   
                      </button>
                    </div>
                  </td>
                </tr>
              );                
              //   <tr 
              //     key={index} 
              //     className={`transition-colors ${
              //       darkMode 
              //         ? rsaData.status === 'overbought' 
              //           ? 'bg-red-900/20 hover:bg-red-900/30' 
              //           : rsaData.status === 'oversold' 
              //           ? 'bg-green-900/20 hover:bg-green-900/30'
              //           : 'bg-gray-700/50 hover:bg-gray-700/70'
              //         : rsaData.status === 'overbought' 
              //         ? 'bg-red-50 hover:bg-red-100' 
              //         : rsaData.status === 'oversold' 
              //         ? 'bg-green-50 hover:bg-green-100'
              //         : 'bg-white hover:bg-gray-50'
              //     }`}
              //   >
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className="text-sm font-bold">{stock.symbol}</div>
              //     </td>
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className="text-sm">{stock.name}</div>
              //     </td>
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className="text-sm">{stock.quantity}</div>
              //     </td>
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className="text-sm">{formatCurrency(stock.average_cost)}</div>
              //     </td>
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className="text-sm font-bold">
              //         {formatCurrency(stock.currentPrice)}
              //       </div>
              //     </td>
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className="text-sm font-bold">
              //         {formatCurrency(stock.marketValue)}
              //       </div>
              //     </td>
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className={`text-sm font-bold ${
              //         profitLoss >= 0 ? 'text-green-600' : 'text-red-600'
              //       }`}>
              //         {formatCurrency(profitLoss)}
              //       </div>
              //     </td>
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className={`text-sm font-bold ${
              //         profitLossPercent >= 0 ? 'text-green-600' : 'text-red-600'
              //       }`}>
              //         {formatPercentage(profitLossPercent)}
              //       </div>
              //     </td>
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              //         rsaData.status === 'overbought' 
              //           ? 'bg-red-100 text-red-800'
              //           : rsaData.status === 'oversold'
              //           ? 'bg-green-100 text-green-800'
              //           : 'bg-gray-100 text-gray-800'
              //       }`}>
              //         {rsaData.status} ({rsaData.value})
              //       </div>
              //     </td>
              //     <td className="px-6 py-4 whitespace-nowrap">
              //       <div className="flex space-x-2">
              //         <button
              //           onClick={(e) => handleButtonClick('setAlert', stock.symbol, e)}
              //           className={`p-1 rounded ${
              //             darkMode 
              //               ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
              //               : 'bg-yellow-500 text-white hover:bg-yellow-600'
              //           }`}
              //           title="Set Price Alert"
              //         >
              //           <Bell size={14} />
              //         </button>
              //         <button
              //           className={`p-1 rounded ${
              //             darkMode 
              //               ? 'bg-blue-600 text-white hover:bg-blue-700' 
              //               : 'bg-blue-500 text-white hover:bg-blue-600'
              //           }`}
              //           title="View Analysis"
              //         >
              //           <BarChart3 size={14} />
              //         </button>
              //       </div>
              //     </td>
              //   </tr>
              // );
            })}
          </tbody>
        </table>
      </div>
      {enhancedPortfolio.length === 0 && (
        <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <Eye size={48} className="mx-auto mb-4 opacity-50" />
          <p>No holdings in portfolio</p>
        </div>
      )}

      {/* Data Status Footer */}
      <div className={`p-3 text-xs border-t ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
        <div className="flex items-center justify-between">
          <span>
            {Object.keys(realTimePrices).length > 0 
              ? `Real-time data for ${Object.keys(realTimePrices).length} symbols` 
              : 'Using delayed data'}
          </span>
          <span>Total: {enhancedPortfolio.length} holdings</span>
        </div>
      </div>      
    </div>
  );
};

export default StockTable;